import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getLocaleISOCode, getLocaleUrlPath } from 'utils';
import { useSeoMeta } from './useSeoMeta';

export const usePageSchema = ({
  location,
  mainEntity = [],
  mainEntityName = null,
  defaultMetaTitle = '',
  defaultMetaDescription = '',
}) => {
  const intl = useIntl();
  const { locale } = intl;

  const { metaTitle, metaDescription, pageTitle } = useSeoMeta();

  const formattedMetaTitle = metaTitle || defaultMetaTitle;

  const formattedMetaDescription = metaDescription || defaultMetaDescription;

  const formattedTitle = pageTitle || formattedMetaTitle;

  const pageSchema = useMemo(() => {
    return {
      '@context': 'http://schema.org',
      '@type': 'WebPage',
      name: formattedMetaTitle,
      description: formattedMetaDescription,
      url: `https://minfin.com.ua${getLocaleUrlPath(locale)}${
        process.env.APP_ROUTER_BASENAME
      }${location.pathname}`,
      inLanguage: getLocaleISOCode(locale),
      isPartOf: {
        '@type': 'WebSite',
        url: 'https://minfin.com.ua/',
        publisher: {
          '@context': 'http://schema.org',
          '@type': 'Organization',
          name: 'Minfin.com.ua',
          knowsLanguage: ['uk-UA', 'ru-UA'],
          description: intl.formatMessage({ id: 'publisher' }),
          url: 'https://minfin.com.ua/',
          logo: {
            '@type': 'ImageObject',
            url: 'https://minfin.com.ua/i/redesign/minfin-logo-ru.png',
          },
          foundingDate: '2008',
          sameAs: [
            'https://www.facebook.com/minfin.com.ua/',
            'https://t.me/Minfin_com_ua/',
            'https://www.youtube.com/channel/UCEW1QlFX_ito4gd3QXxyt5Q/',
            'https://twitter.com/MinfinComUa/',
            'https://www.instagram.com/minfin.com.ua/',
          ],
          address: {
            '@type': 'PostalAddress',
            addressCountry: {
              '@type': 'Country',
              name: intl.formatMessage({
                id: 'pageSchema.addressCountry.name',
              }),
            },
            addressLocality: intl.formatMessage({
              id: 'pageSchema.addressLocality',
            }),
            postalCode: '03057',
            streetAddress: intl.formatMessage({
              id: 'pageSchema.streetAddress',
            }),
          },
          contactPoint: [
            {
              '@type': 'ContactPoint',
              telephone: '+38(044)392-47-40',
              contactType: 'customer service',
              areaServed: 'UA',
              availableLanguage: ['Ukrainian', 'Russian'],
              email: 'info@minfin.com.ua',
            },
          ],
        },
      },
      ...(mainEntity && mainEntity?.length
        ? {
            mainEntity: {
              '@type': 'ItemList',
              name: formattedTitle,
              itemListElement: mainEntity.map(el => {
                return {
                  '@type': 'ExchangeRateSpecification',
                  currency: el.code,
                  name: mainEntityName || 'Курс НБУ',
                  currentExchangeRate: {
                    '@type': 'UnitPriceSpecification',
                    price: el?.rate || el?.ask || 0,
                    priceCurrency: 'UAH',
                  },
                };
              }),
            },
          }
        : {}),
    };
  }, [
    formattedMetaTitle,
    formattedMetaDescription,
    formattedTitle,
    intl,
    locale,
    location.pathname,
    mainEntity,
    mainEntityName,
  ]);

  const pageScripts = useMemo(
    () => [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(pageSchema),
      },
    ],
    [pageSchema]
  );

  return {
    pageSchema,
    pageScripts,
    formattedMetaTitle,
    formattedMetaDescription,
  };
};

export default usePageSchema;
